import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight reveal-on-view wrapper. Drop-in replacement for the
 * framer-motion `whileInView` fade-up pattern used across home sections.
 * ~0.4KB vs ~110KB for framer-motion.
 *
 * Behavior choices that matter for LCP/CLS:
 * - Default rendered state is opacity:1 (no transform). Content paints
 *   immediately on first render and remains in flow. We never block FCP
 *   or LCP with an opacity:0 -> 1 transition.
 * - The fade-up animation is applied as a CSS keyframe AFTER an
 *   IntersectionObserver fires (or immediately for elements already in
 *   the viewport). For elements already in view on mount, we skip the
 *   animation entirely to avoid any post-paint repaint.
 * - prefers-reduced-motion: no animation at all.
 */
export default function Reveal({
  as: Tag = "div",
  delay = 0,
  y = 20,
  className = "",
  style,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);
  const [skip, setSkip] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const node = ref.current;
    if (reduce || !node) {
      setSkip(true);
      return;
    }
    // Already in viewport on mount: skip animation entirely (no repaint).
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setSkip(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setAnimate(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const mergedStyle = animate && !skip
    ? {
        animation: `tn-reveal-up 480ms ease-out ${delay}ms both`,
        // CSS variable so the keyframe can read the requested distance
        "--tn-reveal-y": `${y}px`,
        ...style,
      }
    : style;

  return (
    <Tag ref={ref} className={className} style={mergedStyle} {...rest}>
      {children}
    </Tag>
  );
}
